import React from "react";
import PropTypes from "prop-types";
import { createPortal } from "react-dom";

/**
 * Generate Script Modal Component
 * Modal for generating protection scripts with database type and method selection
 */
export const GenerateScriptModal = ({
  isOpen,
  isGenerating,
  selectedColumnsCount,
  selectedDbType,
  scriptProtectionCategory,
  selectedScriptProtectionType,
  scriptProtectionMethods,
  scriptDownloadUrl,
  protectionCategoryRef,
  protectionMethodRef,
  scriptDownloadRef,
  onDbTypeSelect,
  onProtectionCategorySelect,
  onMethodSelect,
  onGenerateScript,
  onClose,
}) => {
  if (!isOpen) return null;

  const dbTypes = [
    { id: "postgres", label: "PostgreSQL" },
    { id: "sql", label: "SQL Server" },
    { id: "db2", label: "DB2" },
    { id: "oracle", label: "Oracle" },
    { id: "snowflake", label: "Snowflake" },
    { id: "msfabric", label: "MS Fabric" },
    { id: "sap_s4hana", label: "SAP S/4HANA" },
    { id: "sap_datasphere", label: "SAP Datasphere" },
    { id: "databricks", label: "Databricks" },
    { id: "python", label: "Python" },
  ];

  return createPortal(
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-[999999]"
        onClick={onClose}
      />
      <div className="fixed inset-0 flex items-center justify-center z-[999999] p-4 pointer-events-none">
        <div
          className="bg-white rounded-2xl shadow-2xl max-w-xl w-full max-h-[90vh] pointer-events-auto flex flex-col"
          style={{ overflow: "hidden" }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
            <div className="flex items-center gap-3">
              <svg
                className="w-6 h-6 text-purple-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
                />
              </svg>
              <div>
                <h3 className="text-lg font-semibold text-gray-800">
                  Generate Protection Script
                </h3>
                <p className="text-sm text-gray-500">
                  {selectedColumnsCount} column
                  {selectedColumnsCount > 1 ? "s" : ""} selected
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Body */}
          <div className="p-6 space-y-6 overflow-y-auto flex-1">
            {/* DB Type Selection */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Database Type <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-3 gap-2">
                {dbTypes.map((db) => (
                  <button
                    key={db.id}
                    onClick={() => onDbTypeSelect(db.id)}
                    className={`px-3 py-2.5 border-2 rounded-lg text-sm font-medium transition-all text-left ${selectedDbType === db.id
                      ? "border-purple-600 bg-purple-50 text-purple-700"
                      : "border-gray-200 text-gray-700 hover:border-purple-300 hover:bg-gray-50"
                      }`}
                  >
                    {db.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Protection Category */}
            <div ref={protectionCategoryRef}>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Protection Category <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => onProtectionCategorySelect("reversible")}
                  className={`px-4 py-2 border-2 rounded-xl text-sm font-medium transition-all ${scriptProtectionCategory === "reversible"
                    ? "border-purple-600 bg-purple-50 text-purple-700"
                    : "border-gray-200 text-gray-700 hover:border-purple-300"
                    }`}
                >
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Reversible
                  </div>
                  <p className="text-xs text-gray-500 font-normal mt-0">
                    Can be decrypted
                  </p>
                </button>
                <button
                  onClick={() => onProtectionCategorySelect("irreversible")}
                  className={`px-4 py-3 border-2 rounded-xl text-sm font-medium transition-all ${scriptProtectionCategory === "irreversible"
                    ? "border-purple-600 bg-purple-50 text-purple-700"
                    : "border-gray-200 text-gray-700 hover:border-purple-300"
                    }`}
                >
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    Irreversible
                  </div>
                  <p className="text-xs text-gray-500 font-normal mt-1">
                    Permanently protected
                  </p>
                </button>
              </div>
            </div>

            {/* Protection Method */}
            {scriptProtectionCategory && (
              <div ref={protectionMethodRef}>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Protection Method<span className="text-red-500">*</span>
                </label>
                <div className="space-y-2">
                  {(scriptProtectionCategory === "reversible"
                    ? scriptProtectionMethods.reversible
                    : scriptProtectionMethods.irreversible
                  ).map((method) => (
                    <div
                      key={method.name}
                      onClick={() => onMethodSelect(method.name)}
                      className={`border-2 rounded-xl p-4 cursor-pointer transition-all ${selectedScriptProtectionType === method.name
                        ? "border-purple-600 bg-purple-50 shadow-sm"
                        : "border-gray-200 hover:border-purple-300 hover:bg-gray-50"
                        }`}
                    >
                      <div className="flex items-start gap-3">
                        <input
                          type="radio"
                          checked={selectedScriptProtectionType === method.name}
                          onChange={() => undefined}
                          className="w-4 h-4 text-purple-600 mt-0.5"
                        />
                        <div>
                          <p className="text-sm font-semibold text-gray-800">
                            {method.display_name}
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {method.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Download Result */}
            {scriptDownloadUrl && (
              <>
                <div
                  ref={scriptDownloadRef}
                  className="border-2 border-green-200 bg-green-50 rounded-xl p-4 flex items-center justify-between"
                >
                  <div className="flex items-center gap-3">
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-sm font-semibold text-green-800">
                      Script generated successfully!
                    </p>
                  </div>
                  <a
                    href={scriptDownloadUrl}
                    download={`protection_script_${selectedDbType}.sql`}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Download Script
                  </a>
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex items-center justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-all font-medium"
            >
              Cancel
            </button>
            <button
              onClick={onGenerateScript}
              disabled={
                !selectedDbType ||
                !selectedScriptProtectionType ||
                isGenerating ||
                !!scriptDownloadUrl
              }
              className="px-6 py-2 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center gap-2"
            >
              {isGenerating ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                  </svg>
                  Generate Script
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
};

GenerateScriptModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  isGenerating: PropTypes.bool.isRequired,
  selectedColumnsCount: PropTypes.number.isRequired,
  selectedDbType: PropTypes.string,
  scriptProtectionCategory: PropTypes.string,
  selectedScriptProtectionType: PropTypes.string,
  scriptProtectionMethods: PropTypes.arrayOf(PropTypes.object).isRequired,
  scriptDownloadUrl: PropTypes.string,
  protectionCategoryRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]),
  protectionMethodRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]),
  scriptDownloadRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]),
  onDbTypeSelect: PropTypes.func.isRequired,
  onProtectionCategorySelect: PropTypes.func.isRequired,
  onMethodSelect: PropTypes.func.isRequired,
  onGenerateScript: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};
